/**
 * Step 6 — Approval Workflows [TYPE B — Optional]
 *
 * Visibility gate: only shown if canManageApprovalWorkflows() returns true.
 * Empty state by default → "Set Up Workflows Now" → embedded ApprovalTestPage
 * (the exact same screen as Settings → Approvals).
 * No react-router-dom navigate() used for any transition.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WizardBottomBar } from '@/components/onboarding/WizardBottomBar';
import { ApprovalTestPage } from '@/components/Pages/ApprovalTestPage';
import * as workflowService from '@/services/workflowService';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import type { OnboardingStep } from '@/contexts/OnboardingContext';

const PRIMARY = '#00796B';

interface Step6ApprovalWorkflowsProps {
  onNext: (status: 'completed' | 'skipped') => void;
  onBack: () => void;
}

export function Step6ApprovalWorkflows({ onNext, onBack }: Step6ApprovalWorkflowsProps) {
  const { isLive } = useScreenDataMode('settings-approvals');
  const [showEmbedded, setShowEmbedded] = useState(false);
  const [workflowCount, setWorkflowCount] = useState(0);
  const embedRef = useRef<HTMLDivElement>(null);

  const checkWorkflowCount = useCallback(async () => {
    try {
      if (isLive) {
        const wfs = await workflowService.getAllWorkflows();
        setWorkflowCount(wfs.length);
      } else {
        setWorkflowCount(5);
      }
    } catch {
      setWorkflowCount(0);
    }
  }, [isLive]);

  useEffect(() => {
    if (showEmbedded) checkWorkflowCount();
  }, [showEmbedded, checkWorkflowCount]);

  // Hide only the title/icon part of the PageHeader, keeping the "New Workflow" button visible.
  // Also hide the stats grid inside ApprovalTestPage.
  useEffect(() => {
    if (!showEmbedded) return;

    const hide = () => {
      const container = embedRef.current;
      if (!container) return;
      const root = container.querySelector<HTMLElement>('div.space-y-6');
      if (!root) return;

      // Hide just the title/icon left side of the PageHeader, keep the actions (New Workflow button)
      const header = root.querySelector<HTMLElement>('[data-slot="page-header-block"]');
      if (header) {
        const titleRow = header.querySelector<HTMLElement>('.flex.flex-row.items-center.justify-between');
        if (titleRow) {
          const left = titleRow.querySelector<HTMLElement>('.flex.gap-3');
          if (left) left.style.display = 'none';
          titleRow.style.justifyContent = 'flex-end';
        }
        const breadcrumb = header.querySelector<HTMLElement>('[data-slot="breadcrumb"]');
        if (breadcrumb) breadcrumb.style.display = 'none';
        header.style.paddingBottom = '0';
        header.style.marginBottom = '0';
      }

      // Hide the stats grid (direct child of space-y-6 that contains "Active Workflows")
      Array.from(root.children).forEach((child) => {
        if (child.querySelector(':scope p')?.textContent?.includes('Active Workflows')) {
          (child as HTMLElement).style.display = 'none';
        }
      });
    };

    hide();
    const t1 = setTimeout(hide, 100);
    const t2 = setTimeout(hide, 400);
    const t3 = setTimeout(hide, 900);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [showEmbedded]);

  const handleSkip = () => onNext('skipped');
  const handleNext = () => onNext(workflowCount > 0 ? 'completed' : 'skipped');

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-1">
        {!showEmbedded ? (
          <div className="flex flex-col items-center justify-center h-full text-center max-w-md mx-auto">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: `${PRIMARY}18` }}
            >
              <GitBranch className="w-7 h-7" style={{ color: PRIMARY }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No approval workflows created
            </h3>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Approval workflows define who reviews and approves timesheets before they are
              finalised. Without a workflow, timesheets will not go through an approval process. You
              can configure this later from Settings → Workflows.
            </p>
            <Button
              style={{ backgroundColor: PRIMARY, color: 'white' }}
              onClick={() => setShowEmbedded(true)}
            >
              Set Up Workflows Now
            </Button>
          </div>
        ) : (
          <div
            ref={embedRef}
            className="flex flex-col h-full min-h-0 border border-border rounded-lg bg-white overflow-hidden"
          >
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              <ApprovalTestPage />
            </div>
          </div>
        )}
      </div>

      <WizardBottomBar
        currentStep={6 as OnboardingStep}
        hideBack={false}
        hideSkip={!showEmbedded}
        onBack={onBack}
        onSkip={handleSkip}
        onNext={handleNext}
      />
    </div>
  );
}
